import axios from 'axios';
import api from './api';

// Usa la instancia compartida de services/api.js. Antes este servicio tenía su
// propio cliente de axios cuyo interceptor, ante un 401, borraba el token y
// hacía window.location.href = '/login' sin intentar renovar la sesión. El
// access token dura 15 minutos: una búsqueda CIE-10 con el token caducado
// sacaba al médico de la página y se perdía la historia que estaba escribiendo.
// La instancia compartida renueva la sesión y reintenta la petición.
//
// Rutas relativas sin '/api': la baseURL de `api` ya lo incluye
// (REACT_APP_API_URL=https://.../api, o http://localhost:5000/api en local).

/**
 * Servicio para interactuar con la API de CIE-10
 */
const cie10Service = {
  /**
   * Buscar códigos CIE-10
   * @param {Object} params - Parámetros de búsqueda
   * @param {string} params.q - Texto de búsqueda
   * @param {number} params.limit - Cantidad de resultados (default: 20)
   * @param {number} params.skip - Resultados a saltar
   * @param {string} params.type - Filtrar por tipo ('diagnosis', 'symptom', 'other')
   * @param {string} params.chapter - Filtrar por capítulo
   * @param {Object} options - Opciones adicionales (ej: signal para AbortController)
   * @returns {Promise<Object>} - { data, pagination }
   */
  search: async (params = {}, options = {}) => {
    try {
      const response = await api.get('/cie10/search', {
        params,
        ...options,
      });
      return response.data;
    } catch (error) {
      // Cancelar una búsqueda vieja al teclear es lo normal, no un error.
      if (!axios.isCancel(error)) {
        console.error('❌ Error en cie10Service.search:', error);
      }
      throw error;
    }
  },

  /**
   * Obtener un código CIE-10 específico
   * @param {string} code - Código CIE-10 (ej: 'E10.9')
   * @returns {Promise<Object>} - Datos del código
   */
  getByCode: async (code) => {
    try {
      // URL-encode the code parameter for safety
      const encodedCode = encodeURIComponent(code);
      const response = await api.get(`/cie10/code/${encodedCode}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Error en cie10Service.getByCode(${code}):`, error);
      throw error;
    }
  },

  /**
   * Obtener lista de capítulos CIE-10
   * @returns {Promise<Array>} - Lista de capítulos
   */
  getChapters: async () => {
    try {
      const response = await api.get('/cie10/chapters');
      return response.data;
    } catch (error) {
      console.error('❌ Error en cie10Service.getChapters:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de la base de datos CIE-10
   * @returns {Promise<Object>} - Estadísticas
   */
  getStats: async () => {
    try {
      const response = await api.get('/cie10/stats');
      return response.data;
    } catch (error) {
      console.error('❌ Error en cie10Service.getStats:', error);
      throw error;
    }
  },
};

export default cie10Service;
